import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import {
  Dotdotdot,
  ClampedLinkWorkaround,
} from '@hivebrite/design-system/Dotdotdot';
import { Image } from '@hivebrite/design-system/Image';
import { FakeLink } from '@hivebrite/design-system/Link';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { linkControlledHoverEffect } from '@hivebrite/design-system/Theme';

import { AttendeesRow } from 'hb-react/shared/apps/Events/components/AttendeesRow';
import EventCanceledLabel from 'hb-react/shared/apps/Events/components/EventCanceledLabel';
import EventCoverPicture from 'hb-react/shared/apps/Events/components/EventCoverPicture';
import { EventWebinarUrl } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventWebinarUrl';
import EventPricingTag from 'hb-react/shared/apps/Events/components/EventPricingTag';
import EventCardBlocked from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventCardBlocked';
import EventCardLoading from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventCardLoading';
import EventDate from 'hb-react/shared/components/EventDate';
import TwoDigitsCalendar from 'hb-react/shared/icons/dynamic/TwoDigitsCalendar';
import Zoom from 'hb-react/shared/icons/Zoom';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const EventCard = forwardRef(
  (
    {
      event,
      onClick,
      selected,
      hoverStyle,
      blocked,
      loading,
      timezoneConfiguration,
    },
    ref,
  ) => {
    const {
      start_date: startDate,
      start_date_timestamp: startDateTimestamp,
      end_date: endDate,
      end_date_timestamp: endDateTimestamp,
      title,
      locations,
      location,
      show_webinar_url: showWebinarUrl,
      webinar_url: webinarUrl,
      timezone,
      network_categories: networkCategories = [],
      organizer,
      is_internal_zoom_meeting: isInternalZoomMeeting = false,
      new_logo: newLogo,
      new_organizer_logo: newOrganizerLogo,
    } = event;

    const { currentUserTimezone, eventDatesInUserTimezone } =
      timezoneConfiguration || {};

    const eventDisplayTimeZone =
      eventDatesInUserTimezone && currentUserTimezone
        ? currentUserTimezone
        : timezone;

    const day = useMemo(() => {
      if (startDateTimestamp) {
        const dateObj = dayjs(startDateTimestamp).tz(eventDisplayTimeZone);

        return dateObj.format('DD');
      }

      return dayjs(startDate).tz(eventDisplayTimeZone).date();
    }, [startDate, startDateTimestamp, eventDisplayTimeZone]);

    const firstLocation =
      location || (Array.isArray(locations) ? locations[0] : locations);

    const address = firstLocation?.address ?? '';

    if (blocked) {
      return <EventCardBlocked user={event.user} />;
    }

    if (loading) {
      return <EventCardLoading />;
    }

    const handleClick = () => onClick?.(event);

    const avatar = newLogo ? newLogo?.medium_url : newOrganizerLogo?.medium_url;

    return (
      <StyledCard
        onClick={handleClick}
        onKeyPress={onEnterOrSpacePress(handleClick)}
        selected={selected}
        hoverStyle={hoverStyle}
        ref={ref}
        data-testid="event-card"
      >
        <Box position="relative" height={{ _: 170, m: 149, l: 157, xl: 168 }}>
          <EventCoverPictureWrapper
            position="relative"
            width="100%"
            overflow="hidden"
            opacity={event.cancelled ? 0.4 : 1}
          >
            <EventCoverPicture
              dataTestId="event-cover-wrapper"
              coverPicture={event.new_cover_picture?.preview_url}
            />
            {avatar && (
              <AvatarWrapper
                aria-hidden
                dataTestId="event-avatar"
                position="absolute"
                top={3}
                right={3}
              >
                {/*
                Temporarily adding a call to decodeURIComponent because it's returning an encoded URL
                This is because the URL is being returned encoded from the backend and it doesn't show the image in the frontend
                TODO: fix this in the backend so it doesn't return an encoded string

                */}
                <Image src={decodeURIComponent(avatar)} alt={organizer?.name} />
              </AvatarWrapper>
            )}
            {isInternalZoomMeeting && (
              <Box position="absolute" left={3} bottom={3}>
                <Zoom width="48" height="48" />
              </Box>
            )}
          </EventCoverPictureWrapper>
          <Box position="absolute" top={2} left={2}>
            {event.cancelled ? (
              <EventCanceledLabel event={event} />
            ) : (
              <EventPricingTag event={event} />
            )}
          </Box>
        </Box>
        <Box px={4} pt={8} pb={6} flexDirection="column" flex={1}>
          <ContentRow mt={0} alignItems="center" color="primary">
            <StyledTwoDigitsCalendar
              day={day}
              width={42}
              height={42}
              fontWeight={1}
            />
            <EventTitle>
              <ScreenReadersContent>
                {s__('EventsAccessibility|Navigate to')}
              </ScreenReadersContent>
              <ClampedLinkWorkaround
                clamp={2}
                lineHeight="2rem"
                margin={0}
                padding={0}
                title={title}
              >
                {title}
              </ClampedLinkWorkaround>
            </EventTitle>
          </ContentRow>
          <ScreenReadersContent>
            {s__('EventsAccessibility|Published by %{name}').i__({
              name: organizer?.name,
            })}
          </ScreenReadersContent>
          <ContentRow mt={3}>
            <StyledLabel title={__('Date')} />
            <EventDate
              timezoneConfiguration={timezoneConfiguration}
              event={{
                start_date: startDateTimestamp ?? startDate,
                end_date: endDateTimestamp ?? endDate,
                timezone,
              }}
              clamp={1}
            />
          </ContentRow>
          {address && (
            <ContentRow>
              <StyledLabel title={__('Location')} />
              <ClampedText clamp={1}>{address}</ClampedText>
            </ContentRow>
          )}
          {webinarUrl && (
            <ContentRow>
              <StyledLabel title={__('Online event')} />
              <EventWebinarUrl
                webinar_url={webinarUrl}
                show_webinar_url={showWebinarUrl}
                ml={1}
              />
            </ContentRow>
          )}
          <AttendeesRow event={event} />
          {networkCategories?.length > 0 && (
            <Box mt={1}>
              <ScreenReadersContent>{__('Tags')}:</ScreenReadersContent>
              <PillsList
                pills={networkCategories}
                seeMoreLabel={__('See More')}
                seeLessLabel={s__('Tags|See less')}
              />
            </Box>
          )}
        </Box>
      </StyledCard>
    );
  },
);

export default EventCard;

// COMPONENTS
const Label = ({ title, className }) => (
  <Box font="body-semibold-asphalt" className={className}>
    {s__('%{text}:').i__({ text: title })}
  </Box>
);

const ClampedText = ({ children, clamp, title, className }) => (
  <Dotdotdot clamp={clamp} className={className}>
    <span title={title ?? children}>{children}</span>
  </Dotdotdot>
);

// STYLED COMPONENTS
const ContentRow = styled(Box).attrs(({ mt }) => ({
  mt: mt ?? 2,
}))`
  line-height: ${({ theme }) => theme.lineHeights[3]};
`;

const StyledLabel = styled(Label)`
  display: inline-block;
  line-height: ${({ theme }) => theme.lineHeights[3]};
  margin-right: ${({ theme }) => theme.space[1]}px;
  white-space: nowrap;
`;

const StyledTwoDigitsCalendar = styled(TwoDigitsCalendar)`
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin-top: 2px;
  margin-bottom: 2px;
`;

const EventTitle = styled(FakeLink).attrs(() => ({
  ml: 3,
  flex: 1,
  font: { _: 'h4-semibold', l: 'h3-semibold' },
}))`
  max-width: calc(100% - 42px);
  text-decoration: none;
`;

const StyledCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['hoverStyle', 'selected'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
  cursor: pointer;
  height: 100%;
  outline: ${({ theme, selected }) =>
    selected ? `2px solid ${theme.colors.primary} !important` : 'none'};
  &:hover {
    ${({ hoverStyle }) => hoverStyle}
  }
  &:hover ${EventTitle} {
    ${linkControlledHoverEffect()}
  }
  & ${EventCoverPicture} {
    transition: transform 0.3s;
    transform: scale(1);
  }
  &:hover ${EventCoverPicture} {
    transform: scale(1.05);
  }
  & a:hover {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const AvatarWrapper = styled(Box).attrs(() => ({
  height: { _: 48 },
  width: { _: 48 },
}))`
  border-radius: 50%;
  user-select: none;
  border: 2px solid #fff;

  img {
    border-radius: 50%;
    width: inherit;
  }
`;

const EventCoverPictureWrapper = styled(Box)`
  border-top-left-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border-top-right-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;
